import type {ReactElement} from 'react'
import React, {useEffect, useState, useCallback, useRef} from 'react'
import {renderToString} from 'react-dom/server'
import {GoogleMapsMarkerClusterer} from '@react-google-maps/api'
import {calculateDistance, pushToDataLayer} from '@/utils/analytics'
import type {Marker} from '@/_new-code/models/page-props'
import {LoadingOverlay} from '../disease-map/loading-overlay'
import {Map} from './google-map'
import {MarkerDetailedCard} from './marker-detail-card'

interface BusinessMapProps {
	defaultBounds: {
		south: number
		west: number
		north: number
		east: number
	}
	description: string
	isLoaded: boolean
	directionsText: string
	markers: Marker[]
	searchLocation?: {
		lat: number
		lng: number
	} | null
	title: string
	enterLocationPrompt: string
	radius: number
	searchAreaAccentColour: string
	searchAreaOpacity: number | null
}

export const BusinessMap = ({
	markers = [],
	title,
	description,
	isLoaded,
	defaultBounds,
	searchLocation,
	directionsText,
	enterLocationPrompt,
	radius,
	searchAreaAccentColour,
	searchAreaOpacity,
}: BusinessMapProps): ReactElement => {
	const [map, setMap] = useState<google.maps.Map | null>(null)
	const [activeMarker, setActiveMarker] = useState<Marker | null>(null)
	const mapMarkersRef = useRef<google.maps.Marker[]>([])
	const locationMarkerRef = useRef<google.maps.Marker | null>(null)
	const markerClustersRef =
		useRef<GoogleMapsMarkerClusterer.MarkerClusterer | null>(null)
	const infoWindowRef = useRef<google.maps.InfoWindow | null>(null)

	const renderMarkers = useCallback((): void => {
		infoWindowRef.current = new window.google.maps.InfoWindow()
		let getDirectionsLinks: NodeListOf<Element>
		const handleLocationClick = (): void => {
			pushToDataLayer({
				event: 'finder_usage',
				finder_interaction: 'Location Selected',
				finder_url: window.location.href,
			})
		}
		infoWindowRef.current.addListener('closeclick', () => {
			getDirectionsLinks.forEach((a) => {
				a.removeEventListener('click', handleLocationClick)
			})
			setActiveMarker(null)
		})

		markers.forEach((marker) => {
			const mapMarker = new window.google.maps.Marker({
				position: {lat: marker.lat || 0, lng: marker.lng || 0},
				map,
				icon: marker.image,
			})
			mapMarker.addListener('click', () => {
				setActiveMarker(marker)

				infoWindowRef.current?.setContent(
					renderToString(
						<MarkerDetailedCard
							address={marker.address}
							directions={{
								text: directionsText,
								searchLocation,
								lat: marker.lat,
								lng: marker.lng,
							}}
							name={marker.name}
							phone={marker.accountPhone}
							websiteUrl={marker.websiteUrl}
						/>
					)
				)

				infoWindowRef.current?.open({
					anchor: mapMarker,
					map,
					shouldFocus: true,
				})

				pushToDataLayer({
					event: 'finder_usage',
					finder_interaction: 'Marker Click',
					finder_url: window.location.href,
				})

				if (infoWindowRef.current) {
					google.maps.event.addListener(
						infoWindowRef.current,
						'domready',
						() => {
							getDirectionsLinks =
								document.querySelectorAll('.get-directions')
							getDirectionsLinks.forEach((a) => {
								a.addEventListener('click', handleLocationClick)
							})
						}
					)
				}
			})
			mapMarkersRef.current.push(mapMarker)
		})

		markerClustersRef.current =
			new GoogleMapsMarkerClusterer.MarkerClusterer({
				map,
				markers: mapMarkersRef.current,
				renderer: {
					render: (cluster) => {
						const clusterSvg = window.btoa(`
			<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">
			<g id="Group_16843" data-name="Group 16843" transform="translate(-1462 -312)">
				<g id="Ellipse_91499" data-name="Ellipse 91499" transform="translate(1464 314)" fill="#fff" stroke="#2772ce" stroke-width="2">
					<circle cx="21" cy="21" r="21" stroke="none"/>
					<circle cx="21" cy="21" r="22" fill="none"/>
				</g>
			</g>
		</svg>`)
						return new window.google.maps.Marker({
							icon: {
								url: `data:image/svg+xml;base64,${clusterSvg}`,
								scaledSize: new window.google.maps.Size(40, 40),
							},
							label: {
								text: String(cluster.count),
								color: 'black',
								fontSize: '12px',
								fontWeight: '700',
							},
							position: cluster.position,
							zIndex:
								Number(window.google.maps.Marker.MAX_ZINDEX) +
								cluster.count,
						})
					},
				},
			})
		markerClustersRef.current.render()

		if (map)
			window.google.maps.event.addListener(map, 'zoom_changed', () => {
				infoWindowRef.current?.close()
				setActiveMarker(null)
			})
	}, [map, markers, directionsText, searchLocation])

	const renderLocationMarker = useCallback((): void => {
		const elancoBrandColormarkerSvg = window.btoa(`
		<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">
		<g id="Group_16843" data-name="Group 16843" transform="translate(-1462 -312)">
		<g id="Ellipse_91499" data-name="Ellipse 91499" transform="translate(1464 314)" fill="#2772ce" stroke="#fff" stroke-width="3">
		<circle cx="21" cy="21" r="21" stroke="none"/>
		<circle cx="21" cy="21" r="22" fill="none"/>
		</g>
		</g>
		</svg>`)

		if (searchLocation) {
			locationMarkerRef.current = new window.google.maps.Marker({
				position: {
					lat: searchLocation.lat,
					lng: searchLocation.lng,
				},
				map,
				icon: {
					url: `data:image/svg+xml;base64,${elancoBrandColormarkerSvg}`,
					scaledSize: new window.google.maps.Size(25, 25),
					labelOrigin: new window.google.maps.Point(25, 16),
				},
			})
		}
	}, [map, searchLocation])

	const zoomToClosestMarkers = useCallback((): void => {
		if (!searchLocation || markers.length < 1) return

		renderLocationMarker()
		const searchLocationLatLng = new window.google.maps.LatLng(
			searchLocation.lat,
			searchLocation.lng
		)
		const distances = markers
			.map((m) => ({
				accountClassification: m.accountClassification,
				lat: m.lat,
				lng: m.lng,
				distance: calculateDistance(
					searchLocation.lat,
					searchLocation.lng,
					m.lat || 0,
					m.lng || 0
				),
			}))
			.sort((a, b) => a.distance - b.distance)
			.slice(0, 3)

		const distanceMarkers = [
			searchLocationLatLng,
			...distances.map(
				(item) =>
					new window.google.maps.LatLng(item.lat || 0, item.lng || 0)
			),
		]

		let bounds = new window.google.maps.LatLngBounds()
		distanceMarkers.forEach((dm) => bounds.extend(dm))
		if (map) {
			map.setCenter(bounds.getCenter())
			map.panToBounds(bounds)
			map.fitBounds(bounds)
		}
		bounds = new window.google.maps.LatLngBounds(null)
	}, [map, markers, searchLocation, renderLocationMarker])

	useEffect(() => {
		if (map) {
			if (searchLocation?.lat !== 0 && searchLocation?.lng !== 0) {
				map.setCenter({
					lat: searchLocation?.lat || 0,
					lng: searchLocation?.lng || 0,
				})
				map.setZoom(8)
			} else {
				map.fitBounds(defaultBounds)
			}
		}
		if (isLoaded && map && markers.length > 0) {
			renderMarkers()
			zoomToClosestMarkers()
		}

		return () => {
			if (mapMarkersRef.current.length > 0) {
				mapMarkersRef.current.forEach((m) => {
					m.setMap(null)
				})
				mapMarkersRef.current = []
			}

			if (markerClustersRef.current)
				markerClustersRef.current.clearMarkers()

			if (infoWindowRef.current) infoWindowRef.current.close()

			if (locationMarkerRef.current)
				locationMarkerRef.current.setMap(null)
		}
	}, [
		map,
		markers,
		searchLocation,
		defaultBounds,
		isLoaded,
		renderMarkers,
		zoomToClosestMarkers,
	])

	const onMapLoad = useCallback(
		(mapInstance: google.maps.Map) => {
			mapInstance.fitBounds(defaultBounds)
			setMap(mapInstance)
		},
		[defaultBounds]
	)

	const handleMapClick = (): void => {
		pushToDataLayer({
			event: 'finder_usage',
			finder_interaction: 'Map Click',
			finder_url: window.location.href,
		})
	}

	return (
		<div className="flex flex-col bg-gray-100 md:flex-row">
			<div className="w-full px-4 py-8 md:m-0 md:w-2/5 lg:w-[30%]">
				<h2 className="mx-2">{title}</h2>
				{activeMarker ? (
					<MarkerDetailedCard
						address={activeMarker.address}
						directions={{
							text: directionsText,
							searchLocation,
							lat: activeMarker.lat,
							lng: activeMarker.lng,
						}}
						name={activeMarker.name}
						phone={activeMarker.accountPhone}
						websiteUrl={activeMarker.websiteUrl}
					/>
				) : (
					<>
						<p className="mx-2 mr-2 mt-4 text-sm leading-relaxed">
							{description}
						</p>
						{enterLocationPrompt ? (
							<div
								className="mx-2 mr-2 mt-4 text-sm leading-relaxed"
								dangerouslySetInnerHTML={{
									__html: enterLocationPrompt,
								}}
							/>
						) : null}
					</>
				)}
			</div>
			<div className="relative mx-auto h-[460px] w-full overflow-hidden shadow-primary md:mx-0 md:mb-0 md:h-[700px] md:w-[700px] md:rounded-none md:shadow-none lg:w-[70%]">
				{!isLoaded && <LoadingOverlay color="#fff" />}
				{isLoaded ? (
					<Map
						containerClassName="w-full h-full"
						initialZoom={8}
						maxZoom={0}
						onClick={handleMapClick}
						onLoad={onMapLoad}
						radius={radius}
						searchAreaAccentColour={searchAreaAccentColour}
						searchAreaOpacity={searchAreaOpacity}
						searchLocation={searchLocation}
						showCircle={markers.length > 0}
					/>
				) : null}
			</div>
		</div>
	)
}
